import * as React from 'react';
import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  Placement as FloatingPlacement,
  shift,
} from '@floating-ui/react';
import clsx from 'clsx';

import {MarkdownReact} from 'common/components/markdown';
import {useTranslation} from 'common/i18n/hooks';
import {Translatable} from 'common/i18n';
import {LocalizationStore} from 'common/i18n/store';
import timing from 'common/stylesheets/mixins/timing.json';

import {ArrowType, DisplaySize, DisplayType, Placement, PositioningStrategy, TooltipTextAlign} from './types';
import css from './index.styl?inline';

export interface TooltipProps {
  text?: Translatable;
  placement?: Placement | FloatingPlacement;
  alwaysShow?: boolean;
  forceShow?: boolean;
  isTooltipTour?: boolean;
  nonBlocking?: boolean;
  breakWord?: boolean;
  skidding?: number;
  offset?: number;
  maxWidth?: string;
  type?: DisplayType;
  arrowType?: ArrowType;
  delayIn?: number;
  delayOut?: number;
  hideOnMouseDown?: boolean;
  imageSrc?: string;
  size?: DisplaySize;
  positionStrategy?: PositioningStrategy;
  allowedFlipDirections?: Placement[];
  textAlign?: TooltipTextAlign;
  parentEl?: HTMLElement;
  lazyContentRenderer?: (i18n: LocalizationStore) => React.ReactNode;
  onShowed?: () => void;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  elref?: string;
  slot?: string;
}

const placements: Record<Placement, FloatingPlacement> = {
  [Placement.Left]: `left-start`,
  [Placement.Right]: `right-start`,
  [Placement.RightCenter]: `right`,
  [Placement.LeftCenter]: `left`,
  [Placement.RightEnd]: `right-end`,
  [Placement.LeftEnd]: `left-end`,
  [Placement.Top]: `top`,
  [Placement.Bottom]: `bottom`,
};

let activeTooltip: {hide: () => void} | null = null;

export function TooltipContent({
  host,
  parentEl,
  reference,
  text,
  placement = Placement.Top,
  alwaysShow = false,
  forceShow = false,
  isTooltipTour = false,
  nonBlocking = false,
  breakWord = false,
  skidding = 0,
  offset: requestedOffset = -1,
  maxWidth,
  type = DisplayType.Default,
  arrowType = ArrowType.Default,
  delayIn = timing[`$mp-default-tooltip-delay-in`],
  delayOut = timing[`$mp-default-tooltip-delay-out`],
  hideOnMouseDown = true,
  imageSrc,
  size = DisplaySize.Fit,
  positionStrategy = PositioningStrategy.Fixed,
  allowedFlipDirections,
  textAlign = TooltipTextAlign.Center,
  lazyContentRenderer,
  onShowed,
  children,
}: TooltipProps & {host: HTMLElement; reference?: React.RefObject<HTMLElement>}) {
  const i18n = useTranslation();
  const trigger = parentEl || reference?.current || host.parentElement;
  const [visibility, setVisibility] = React.useState(`hidden`);
  const [firstInChain, setFirstInChain] = React.useState(true);
  const arrowRef = React.useRef<HTMLDivElement>(null);
  const hideRef = React.useRef<(event: MouseEvent) => void>();
  const showedRef = React.useRef(onShowed);
  showedRef.current = onShowed;
  const tooltipOffset = requestedOffset === -1 ? (arrowType === ArrowType.Default ? 8 : 41) : requestedOffset;

  React.useLayoutEffect(() => {
    host.setAttribute(`data-tooltip`, ``);
    host.setAttribute(`type`, type);
    host.setAttribute(`size`, size);
    return () => {
      host.removeAttribute(`data-tooltip`);
      host.removeAttribute(`type`);
      host.removeAttribute(`size`);
    };
  }, [host, type, size]);

  React.useLayoutEffect(() => {
    if (text) host.setAttribute(`text`, i18n.print(text).toString());
    else host.removeAttribute(`text`);
  }, [host, text, i18n]);

  React.useLayoutEffect(() => {
    host.hidden = visibility === `hidden`;
    host.setAttribute(`aria-hidden`, String(visibility !== `visible`));
    host.setAttribute(`role`, `tooltip`);
  }, [host, visibility]);

  React.useEffect(() => {
    if (!trigger) return undefined;
    let showTimer: ReturnType<typeof setTimeout> | undefined;
    let hideTimer: ReturnType<typeof setTimeout> | undefined;
    let showFrame: number | undefined;
    let disposed = false;
    let visible = false;
    const cancelPending = () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      if (showFrame !== undefined) cancelAnimationFrame(showFrame);
    };
    const controller = {
      hide: () => {
        cancelPending();
        visible = false;
        setVisibility(`hidden`);
        if (activeTooltip === controller) activeTooltip = null;
      },
    };
    const show = () => {
      cancelPending();
      if (visible) return;
      setVisibility(`transitioning`);
      const reveal = () => {
        if (disposed || !host.isConnected || !trigger.isConnected) return;
        if (delayIn && !(alwaysShow || forceShow)) {
          setFirstInChain(!activeTooltip);
          activeTooltip?.hide();
          activeTooltip = controller;
        }
        visible = true;
        setVisibility(`visible`);
        host.dispatchEvent(new CustomEvent(`showed`));
        showedRef.current?.();
      };
      if (delayIn && !activeTooltip) showTimer = setTimeout(reveal, delayIn);
      else showFrame = requestAnimationFrame(reveal);
    };
    const hide = (event: MouseEvent) => {
      const relatedTarget = event.relatedTarget;
      const staysInTooltip =
        relatedTarget instanceof Node && (host.contains(relatedTarget) || host.shadowRoot?.contains(relatedTarget));
      if (isTooltipTour || forceShow || staysInTooltip) return;
      cancelPending();
      if (delayOut) hideTimer = setTimeout(controller.hide, delayOut);
      else controller.hide();
    };
    const cancelHide = () => clearTimeout(hideTimer);
    hideRef.current = hide;
    if (alwaysShow || forceShow) show();
    else {
      trigger.addEventListener(`mouseenter`, show);
      trigger.addEventListener(`mouseleave`, hide);
      if (hideOnMouseDown) trigger.addEventListener(`mousedown`, hide);
    }
    host.addEventListener(`mouseenter`, cancelHide);
    host.addEventListener(`mouseleave`, hide);
    return () => {
      disposed = true;
      cancelPending();
      if (activeTooltip === controller) activeTooltip = null;
      hideRef.current = undefined;
      trigger.removeEventListener(`mouseenter`, show);
      trigger.removeEventListener(`mouseleave`, hide);
      trigger.removeEventListener(`mousedown`, hide);
      host.removeEventListener(`mouseenter`, cancelHide);
      host.removeEventListener(`mouseleave`, hide);
      setVisibility(`hidden`);
    };
  }, [host, trigger, delayIn, delayOut, alwaysShow, forceShow, isTooltipTour, hideOnMouseDown]);

  React.useLayoutEffect(() => {
    if (visibility === `hidden`) return undefined;
    if (!trigger) return undefined;
    let disposed = false;
    const updatePosition = () => {
      if (disposed || !host.isConnected || !trigger.isConnected) return;
      const arrowElement = arrowRef.current;
      void computePosition(trigger, host, {
        placement: placements[placement as Placement] ?? (placement as FloatingPlacement),
        strategy: positionStrategy,
        middleware: [
          shift({padding: 8}),
          offset({mainAxis: tooltipOffset, crossAxis: skidding}),
          arrowElement && arrow({padding: 4, element: arrowElement}),
          flip({
            rootBoundary: `viewport`,
            fallbackStrategy: `bestFit`,
            padding: {top: 0},
            ...(allowedFlipDirections
              ? {fallbackPlacements: allowedFlipDirections.map((direction) => placements[direction])}
              : {}),
          }),
        ],
      }).then(({x, y, placement: finalPlacement, strategy, middlewareData}) => {
        if (disposed || !host.isConnected) return;
        Object.assign(host.style, {
          left: `${x}px`,
          top: `${y}px`,
          position: strategy,
          pointerEvents: nonBlocking ? `none` : ``,
        });
        host.setAttribute(`data-placement`, finalPlacement);
        if (arrowElement && middlewareData.arrow) {
          const {x: arrowX, y: arrowY} = middlewareData.arrow;
          Object.assign(arrowElement.style, {
            left: typeof arrowX === `number` ? `${arrowX}px` : ``,
            top: typeof arrowY === `number` ? `${arrowY}px` : ``,
          });
        }
      });
    };
    const cleanup = autoUpdate(trigger, host, updatePosition);
    return () => {
      disposed = true;
      cleanup();
      host.removeAttribute(`data-placement`);
      for (const property of [`left`, `top`, `position`, `pointer-events`]) host.style.removeProperty(property);
    };
  }, [
    host,
    trigger,
    visibility,
    placement,
    positionStrategy,
    tooltipOffset,
    skidding,
    allowedFlipDirections,
    nonBlocking,
  ]);

  return (
    <>
      <style>{css}</style>
      <div
        aria-hidden={visibility !== `visible`}
        className={clsx(`mp-tooltip`, `is-${visibility}`, `size-${size}`, {
          'is-tooltip-tour': isTooltipTour,
          'is-dot-line-arrow': arrowType === ArrowType.LineDot,
          'has-max-width': (maxWidth !== undefined && maxWidth !== null) || size !== DisplaySize.Fit,
          'is-animated': Boolean(delayIn) && firstInChain,
          'has-image': Boolean(imageSrc),
          'break-word': breakWord,
          'non-blocking': nonBlocking,
        })}
        style={
          {
            maxWidth,
            hyphens: maxWidth ? `auto` : undefined,
            textAlign,
            '--tooltip-offset': `${tooltipOffset}px`,
          } as React.CSSProperties
        }
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onMouseLeave={(event) => hideRef.current?.(event.nativeEvent)}
      >
        {imageSrc && <img className='mp-tooltip-image' src={imageSrc} />}
        <slot>{children}</slot>
        {text && visibility !== `hidden` && (
          <div className='mp-tooltip-text'>
            <MarkdownReact text={i18n.print(text).toString()} />
          </div>
        )}
        {visibility !== `hidden` && lazyContentRenderer?.(i18n)}
        {arrowType === ArrowType.Default && !isTooltipTour && <div className='mp-tooltip-arrow' ref={arrowRef} />}
      </div>
    </>
  );
}
